export type TileType = 'bank_linking' | 'connected_payroll' | 'manual_upload';

export type StepType =
  | 'personal_info'
  | 'proof_of_income'
  | 'supporting_documents'
  | 'review_and_submit'
  | 'success';

export type ActionType = 'back' | 'next' | 'start' | 'submit' | 'edit';

export type ReviewSectionType = 'personal_info' | 'proof_of_income' | 'supporting_documents';

export const generateTileId = (step: StepType, tile: TileType) => {
  return `${step}-${tile}-tile`;
};

export const generateButtonId = (action: ActionType) => {
  return `${action}_button`;
};

export const generateButtonIdForTiles = (tile: TileType, action: ActionType) => {
  return `${tile}-${action}_button`;
};

/**
 * Maps review section titles to review section types
 *
 * Examples:
 * - 'personalInfo.title' -> 'personal_info'
 * - 'proofOfIncome.title' -> 'proof_of_income'
 * - 'supportingDocuments.title' -> 'supporting_documents'
 */
export const getReviewSectionType = (title: string): ReviewSectionType => {
  // TODO: Validate this Jansen
  const titleToSectionMap: Record<string, ReviewSectionType> = {
    'personalInfo.title': 'personal_info',
    'proofOfIncome.title': 'proof_of_income',
    'supportingDocuments.title': 'supporting_documents'
  };

  // Try to find exact match first
  if (titleToSectionMap[title]) {
    return titleToSectionMap[title];
  }

  // Default fallback
  return 'personal_info';
};

/**
 * Generates the data-review-section-id attribute for review sections
 */
export const generateReviewSectionId = (title: string): string => {
  const sectionType = getReviewSectionType(title);
  return `review-section-${sectionType}`;
};

/**
 * Generates a data attribute object for step identification
 */
export const generateStepDataAttributes = (step: StepType) => `data-step-id=${step}`;

/**
 * Generates a data attribute object for tile identification
 */
export const generateTileDataAttributes = (step: StepType, tileType: TileType) => ({
  'data-step-id': step,
  'data-tile-type': tileType,
  'data-tile-id': generateTileId(step, tileType)
});

/**
 * Generates CSS selectors for telemetry tools like Pendo
 */
export const generateTelemetrySelectors = {
  tile: (step: StepType, tileType: TileType) =>
    `div[data-step-id="${step}"] #${generateTileId(step, tileType)}`,

  button: (step: StepType, tileType: TileType, action: ActionType = 'start') =>
    `div[data-step-id="${step}"] #${generateButtonId(action)}`,

  tileButton: (step: StepType, tileType: TileType, action: ActionType = 'start') =>
    `div[data-step-id="${step}"][data-tile-type="${tileType}"] #${generateButtonIdForTiles(tileType, action)}`,

  reviewSection: (sectionType: ReviewSectionType) =>
    `div[data-review-section-id="review-section-${sectionType}"]`
};
